import {
	Delete02Icon,
	GridIcon,
	Package01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, usePaginatedQuery, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { Archive, FileUp, Layers, LayoutPanelTop } from "lucide-react";
import {
	lazy,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { BranchSelector } from "@/components/branch-selector";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { CreatePriceProfileModal } from "@/components/modals/create-price-profile-modal";
import { CreateProductModal } from "@/components/modals/create-product-modal";
import { CreateVariantModal } from "@/components/modals/create-variant-modal";
import { EditPriceProfileModal } from "@/components/modals/edit-price-profile-modal";
import { EditProductModal } from "@/components/modals/edit-product-modal";
import { toast } from "@/lib/toast";

const UploadProductsModal = lazy(
	() => import("@/components/modals/upload-products-modal"),
);

import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogMedia,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DisplaySelect } from "@/components/ui/display-select";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { type Filter, Filters } from "@/components/ui/filters";
import { SearchInput } from "@/components/ui/updated-search-input";
import { PriceProfileCards } from "@/features/products/_components/price-profile-cards";
import { ProductsTable } from "@/features/products/_components/products-table";
import { useDebounce } from "@/hooks/use-debounce";
import { useFilters } from "@/hooks/use-filters";
import { useLocalStorageSettings } from "@/hooks/use-local-storage-settings";
import { useMe } from "@/hooks/use-me";
import { usePagination } from "@/hooks/use-pagination";
import { toSatangs } from "@/lib/currency";
import { handleConvexError } from "@/lib/errors";
import type { PriceProfileInput } from "@/lib/schemas/price-profile";
import type { ProductInput, VariantInput } from "@/lib/schemas/product";
import type { Category } from "@/lib/types/category";
import type { PriceProfile } from "@/lib/types/price-profile";
import type { Product, ProductListItem } from "@/lib/types/product";

export const Route = createFileRoute("/(dashboard)/products")({
	component: ProductsPage,
});

const PAGE_SIZE = 10;

interface DisplaySettings {
	viewMode: "cards" | "rows";
	showHeaders: boolean;
	showDeleted: boolean;
	sortKey: string;
	columnVisibility: Record<string, boolean>;
}

const DEFAULT_SETTINGS: DisplaySettings = {
	viewMode: "rows",
	showHeaders: true,
	showDeleted: false,
	sortKey: "name",
	columnVisibility: {
		category: true,
		price: true,
		status: true,
		priceProfile: true,
	},
};

function ProductsPage() {
	const navigate = useNavigate();
	// Get current user for mutations that require orgId
	const me = useMe();
	const orgId = me?.activeOrganizationId;

	// State
	const [searchQuery, setSearchQuery] = useState("");
	const debouncedSearch = useDebounce(searchQuery);
	const [selectedBranchId, setSelectedBranchId] = useState<
		Id<"branches"> | undefined
	>();

	const { pagination, onPaginationChange, resetPage } =
		usePagination(PAGE_SIZE);

	const {
		settings: displaySettings,
		setSettings: setDisplaySettings,
		hasChanges,
		handleReset,
		handleSetDefault,
		updateColumnVisibility,
	} = useLocalStorageSettings<DisplaySettings>(
		"slappos_products_display_settings",
		DEFAULT_SETTINGS,
		() => {
			const defaultMode =
				globalThis.window !== undefined && globalThis.innerWidth < 640
					? "cards"
					: "rows";
			return { ...DEFAULT_SETTINGS, viewMode: defaultMode };
		},
	);

	const updateSetting = useCallback(
		// biome-ignore format: wuchale parser fix
		<K extends keyof DisplaySettings,>(key: K, value: DisplaySettings[K]) => {
			setDisplaySettings((prev) => ({ ...prev, [key]: value }));
			if (key === "showDeleted") resetPage();
		},
		[setDisplaySettings, resetPage],
	);

	// Price profile filter state
	const [selectedProfileId, setSelectedProfileId] = useState<
		Id<"priceProfiles"> | "none" | null
	>(null);

	// Modal state
	const [createOpen, setCreateOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [isUploadingProducts, setIsUploadingProducts] = useState(false);
	const [variantOpen, setVariantOpen] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [productToDelete, setProductToDelete] = useState<Product | null>(null);

	// Price profile modal state
	const [createProfileOpen, setCreateProfileOpen] = useState(false);
	const [editProfileOpen, setEditProfileOpen] = useState(false);
	const [selectedProfile, setSelectedProfile] = useState<PriceProfile | null>(
		null,
	);

	// Mutations
	const createProduct = useMutation(api.features.products.web.create);
	const updateProduct = useMutation(api.features.products.web.update);
	const deleteProduct = useMutation(api.features.products.web.remove);
	const restoreProduct = useMutation(api.features.products.web.restore);
	const createVariant = useMutation(api.features.products.web.createVariant);
	const createPriceProfile = useMutation(api.features.priceProfiles.web.create);
	const updatePriceProfile = useMutation(api.features.priceProfiles.web.update);
	const removePriceProfile = useMutation(api.features.priceProfiles.web.remove);

	// Mutation loading states
	const [isCreating, setIsCreating] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isRestoring, setIsRestoring] = useState(false);
	const [isCreatingVariant, setIsCreatingVariant] = useState(false);
	const [isCreatingProfile, setIsCreatingProfile] = useState(false);
	const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
	const [isDeletingProfile, setIsDeletingProfile] = useState(false);
	const [profileToDelete, setProfileToDelete] = useState<PriceProfile | null>(
		null,
	);
	const profileDeleteDialogOpen = profileToDelete !== null;

	const handleDownloadTemplate = useCallback(() => {
		const link = document.createElement("a");
		link.href = "/templates/Slap-Product-Import-Template.xlsx";
		link.download = "Slap-Product-Import-Template.xlsx";
		document.body.appendChild(link);
		link.click();
		link.remove();
		toast.success(<>Template download started</>);
	}, []);

	// Queries
	const productsQuery = usePaginatedQuery(
		api.features.products.web.list,
		{
			includeDeleted: displaySettings.showDeleted,
		},
		{ initialNumItems: 50 },
	);
	const categoriesQuery = usePaginatedQuery(
		api.features.categories.web.list,
		{},
		{ initialNumItems: 50 },
	);
	const priceProfilesQuery = useQuery(api.features.priceProfiles.web.list, {});
	const branchesQuery = useQuery(api.features.branches.web.list, {});

	const allProducts = (productsQuery.results ?? []) as ProductListItem[];
	const categories = (categoriesQuery.results ?? []) as Category[];
	const priceProfiles = (priceProfilesQuery ?? []) as PriceProfile[];
	const branches = branchesQuery ?? [];
	const isLoading =
		productsQuery.status === "LoadingFirstPage" ||
		categoriesQuery.status === "LoadingFirstPage" ||
		branchesQuery === undefined;

	// Build filters dynamically based on categories
	const productFilters: Filter[] = useMemo(() => {
		const categoryFilter: Filter = {
			key: "categoryId",
			label: "Category",
			icon: <HugeiconsIcon icon={GridIcon} size={16} strokeWidth={1.5} />,
			multiple: true,
			options: categories.map((cat) => ({
				value: cat._id,
				label: cat.name,
			})),
		};

		const statusFilter: Filter = {
			key: "status",
			label: "Status",
			icon: <Layers className="size-4" />,
			options: [
				{ value: "active", label: "Active" },
				{ value: "inactive", label: "Inactive" },
			],
		};

		return [categoryFilter, statusFilter];
	}, [categories]);

	// Filters
	const filterState = useFilters({
		filters: productFilters,
		onFiltersChange: () => resetPage(),
	});

	// Load more handler for pagination
	const handleLoadMore = useCallback(() => {
		if (productsQuery.status === "CanLoadMore") {
			productsQuery.loadMore(50);
		}
	}, [productsQuery]);

	// Compute product counts and summaries by price profile
	const { productCountsByProfile, profileProductSummaries, noProfileCount } =
		useMemo(() => {
			const counts: Record<string, number> = {};
			const summaries: Record<string, string[]> = {};
			let noProfile = 0;
			allProducts.forEach((p) => {
				if (p.type === "variant") return;

				if (p.priceProfileId) {
					counts[p.priceProfileId] = (counts[p.priceProfileId] ?? 0) + 1;
					if (!summaries[p.priceProfileId]) summaries[p.priceProfileId] = [];
					if (summaries[p.priceProfileId].length < 2) {
						summaries[p.priceProfileId].push(p.name);
					}
				} else {
					noProfile++;
				}
			});

			const resultSummaries: Record<string, string> = {};
			for (const id in summaries) {
				resultSummaries[id] =
					summaries[id].join(", ") +
					(counts[id] > summaries[id].length ? "..." : "");
			}

			return {
				productCountsByProfile: counts,
				profileProductSummaries: resultSummaries,
				noProfileCount: noProfile,
			};
		}, [allProducts]);

	// Filter products based on search, filters, and price profile
	const filteredProducts = useMemo(() => {
		let result = allProducts;

		// Filter out variants from main list (they're shown under parents)
		result = result.filter((p) => p.type !== "variant");

		// Filter by search
		if (debouncedSearch) {
			const query = debouncedSearch.toLowerCase();
			result = result.filter(
				(p) =>
					p.name.toLowerCase().includes(query) ||
					p.sku.toLowerCase().includes(query),
			);
		}

		// Filter by price profile
		if (selectedProfileId === "none") {
			result = result.filter((p) => !p.priceProfileId);
		} else if (selectedProfileId) {
			result = result.filter((p) => p.priceProfileId === selectedProfileId);
		}

		// Filter by branch: show only products stocked or overridden at the selected branch
		if (selectedBranchId) {
			result = result.filter((p) =>
				p.branches.some((b) => b._id === selectedBranchId),
			);
		}

		// Apply active filters
		filterState.activeFilters.forEach((filter) => {
			const values = Array.isArray(filter.value)
				? filter.value
				: [filter.value];
			if (filter.key === "categoryId") {
				result = result.filter((p) => values.includes(p.categoryId));
			} else if (filter.key === "status") {
				result = result.filter((p) => {
					const status = p.isActive ? "active" : "inactive";
					return values.includes(status);
				});
			}
		});

		// Apply sorting
		return [...result].sort((a, b) => {
			if (displaySettings.sortKey === "name") {
				return a.name.localeCompare(b.name);
			}
			if (displaySettings.sortKey === "sku") {
				return a.sku.localeCompare(b.sku);
			}
			if (displaySettings.sortKey === "price") {
				return a.basePriceSatangs - b.basePriceSatangs;
			}
			if (displaySettings.sortKey === "createdAt") {
				return (b._creationTime || 0) - (a._creationTime || 0);
			}
			return 0;
		});
	}, [
		debouncedSearch,
		filterState.activeFilters,
		allProducts,
		selectedProfileId,
		selectedBranchId,
		displaySettings.sortKey,
	]);

	// Paginate the filtered results (client-side)
	const paginatedProducts = useMemo(() => {
		const start = pagination.pageIndex * pagination.pageSize;
		const end = start + pagination.pageSize;
		return filteredProducts.slice(start, end);
	}, [filteredProducts, pagination.pageIndex, pagination.pageSize]);

	// Reset page when debounced search changes (skip initial mount to preserve URL pagination)
	const isFirstRender = useRef(true);
	// biome-ignore lint/correctness/useExhaustiveDependencies: debouncedSearch is the intentional trigger
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		resetPage();
	}, [debouncedSearch, resetPage]);

	// Handlers
	const handleSearchChange = useCallback((value: string) => {
		setSearchQuery(value);
	}, []);

	const handleEdit = useCallback((product: Product) => {
		setSelectedProduct(product);
		setEditOpen(true);
	}, []);

	const handleDuplicate = useCallback(
		async (product: Product) => {
			if (!orgId) return;

			setIsCreating(true);
			try {
				await createProduct({
					orgId,
					name: `${product.name} (Copy)`,
					categoryId: product.categoryId,
					basePriceSatangs: product.basePriceSatangs,
					costPriceSatangs: product.costPriceSatangs,
					description: product.description,
					lowStockThreshold: product.lowStockThreshold,
					trackInventory: product.trackInventory,
					strainProfile: product.strainProfile,
					isActive: product.isActive,
					imagePublicId: product.imagePublicId,
				});
				toast.success(<>Product duplicated</>);
			} catch (error) {
				handleConvexError(error, "Failed to duplicate product");
			} finally {
				setIsCreating(false);
			}
		},
		[orgId, createProduct],
	);

	const handleAddVariant = useCallback((product: Product) => {
		setSelectedProduct(product);
		setVariantOpen(true);
	}, []);

	const handleDelete = useCallback((product: Product) => {
		setProductToDelete(product);
		setDeleteDialogOpen(true);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!productToDelete || !orgId) return;

		setIsDeleting(true);
		try {
			await deleteProduct({
				orgId,
				productId: productToDelete._id,
				hardDelete: false,
			});
			toast.deleted("Product");
			setDeleteDialogOpen(false);
			setProductToDelete(null);
		} catch (error) {
			handleConvexError(error, "Failed to delete product");
		} finally {
			setIsDeleting(false);
		}
	}, [productToDelete, orgId, deleteProduct]);

	const handleRestore = useCallback(
		async (product: Product) => {
			if (!orgId) return;

			setIsRestoring(true);
			try {
				await restoreProduct({ orgId, productId: product._id });
				toast.success(<>Product restored</>);
			} catch (error) {
				handleConvexError(error, "Failed to restore product");
			} finally {
				setIsRestoring(false);
			}
		},
		[orgId, restoreProduct],
	);

	const handleCreateProduct = useCallback(
		async (data: ProductInput) => {
			if (!orgId) return;

			setIsCreating(true);
			try {
				await createProduct({
					orgId,
					name: data.name,
					categoryId: data.categoryId as Id<"categories">,
					sku: data.sku || undefined,
					barcode: data.barcode || undefined,
					description: data.description || undefined,
					basePriceSatangs: toSatangs(data.basePriceBaht),
					costPriceSatangs: data.costPriceBaht
						? toSatangs(data.costPriceBaht)
						: undefined,
					priceProfileId: data.priceProfileId as
						| Id<"priceProfiles">
						| undefined,
					lowStockThreshold: data.lowStockThreshold,
					trackInventory: data.trackInventory,
					strainProfile: data.strainProfile,
					isActive: data.isActive,
					imagePublicId: data.imagePublicId as string | undefined,
				});
				toast.created("Product");
			} catch (error) {
				handleConvexError(error, "Failed to create product");
				throw error;
			} finally {
				setIsCreating(false);
			}
		},
		[orgId, createProduct],
	);

	const handleUpdateProduct = useCallback(
		async (data: ProductInput) => {
			if (!orgId || !selectedProduct) return;

			setIsUpdating(true);
			try {
				await updateProduct({
					orgId,
					productId: selectedProduct._id,
					name: data.name,
					categoryId: data.categoryId as Id<"categories">,
					sku: data.sku || undefined,
					barcode: data.barcode || undefined,
					description: data.description || undefined,
					basePriceSatangs: toSatangs(data.basePriceBaht),
					costPriceSatangs: data.costPriceBaht
						? toSatangs(data.costPriceBaht)
						: undefined,
					priceProfileId: data.priceProfileId as
						| Id<"priceProfiles">
						| undefined,
					lowStockThreshold: data.lowStockThreshold,
					trackInventory: data.trackInventory,
					strainProfile: data.strainProfile,
					isActive: data.isActive,
					imagePublicId: data.imagePublicId as string | undefined,
				});
				toast.updated("Product");
			} catch (error) {
				handleConvexError(error, "Failed to update product");
				throw error;
			} finally {
				setIsUpdating(false);
			}
		},
		[orgId, selectedProduct, updateProduct],
	);

	const handleCreateVariant = useCallback(
		async (data: VariantInput) => {
			if (!orgId || !selectedProduct) return;

			setIsCreatingVariant(true);
			try {
				await createVariant({
					orgId,
					parentProductId: selectedProduct._id,
					variantAttributes: data.variantAttributes,
					sku: data.sku || undefined,
					barcode: data.barcode || undefined,
					name: data.name || undefined,
					basePriceSatangs: data.basePriceBaht
						? toSatangs(data.basePriceBaht)
						: undefined,
					costPriceSatangs: data.costPriceBaht
						? toSatangs(data.costPriceBaht)
						: undefined,
					isActive: data.isActive,
				});
				toast.success(<>Variant created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create variant");
				throw error;
			} finally {
				setIsCreatingVariant(false);
			}
		},
		[orgId, selectedProduct, createVariant],
	);

	// Price profile handlers
	const handleProfileSelect = useCallback(
		(profileId: Id<"priceProfiles"> | "none" | null) => {
			setSelectedProfileId(profileId);
			resetPage();
		},
		[resetPage],
	);

	const handleEditProfile = useCallback((profile: PriceProfile) => {
		setSelectedProfile(profile);
		setEditProfileOpen(true);
	}, []);

	const handleCreatePriceProfile = useCallback(
		async (data: PriceProfileInput) => {
			setIsCreatingProfile(true);
			try {
				await createPriceProfile({
					name: data.name,
					type: data.type,
					tiers: data.tiers.map((tier) => ({
						minQuantity: tier.minQuantity,
						maxQuantity: tier.maxQuantity,
						discountPercent: tier.discountPercent,
						fixedPriceSatangs: tier.fixedPriceBaht
							? toSatangs(tier.fixedPriceBaht)
							: undefined,
					})),
					isMultiple: data.isMultiple,
					singleItemThreshold: data.singleItemThreshold,
					roundingMethod: data.roundingMethod,
					roundingPrecisionSatangs: toSatangs(data.roundingPrecisionBaht),
					isActive: data.isActive,
					branchIds: data.branchIds as Id<"branches">[] | undefined,
				});
				toast.success(<>Price profile created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create price profile");
				throw error;
			} finally {
				setIsCreatingProfile(false);
			}
		},
		[createPriceProfile],
	);

	const handleUpdatePriceProfile = useCallback(
		async (data: PriceProfileInput) => {
			if (!selectedProfile) return;

			setIsUpdatingProfile(true);
			try {
				await updatePriceProfile({
					priceProfileId: selectedProfile._id,
					name: data.name,
					type: data.type,
					tiers: data.tiers.map((tier) => ({
						minQuantity: tier.minQuantity,
						maxQuantity: tier.maxQuantity,
						discountPercent: tier.discountPercent,
						fixedPriceSatangs: tier.fixedPriceBaht
							? toSatangs(tier.fixedPriceBaht)
							: undefined,
					})),
					isMultiple: data.isMultiple,
					singleItemThreshold: data.singleItemThreshold,
					roundingMethod: data.roundingMethod,
					roundingPrecisionSatangs: toSatangs(data.roundingPrecisionBaht),
					isActive: data.isActive,
					branchIds: data.branchIds as Id<"branches">[] | undefined,
				});
				toast.success(<>Price profile updated</>);
			} catch (error) {
				handleConvexError(error, "Failed to update price profile");
				throw error;
			} finally {
				setIsUpdatingProfile(false);
			}
		},
		[selectedProfile, updatePriceProfile],
	);

	const handleDeleteProfile = useCallback((profile: PriceProfile) => {
		setProfileToDelete(profile);
	}, []);

	const confirmDeleteProfile = useCallback(async () => {
		if (!profileToDelete) return;

		setIsDeletingProfile(true);
		try {
			await removePriceProfile({ priceProfileId: profileToDelete._id });
			toast.deleted("Price profile");
			setProfileToDelete(null);
		} catch (error) {
			handleConvexError(error, "Failed to delete price profile");
		} finally {
			setIsDeletingProfile(false);
		}
	}, [profileToDelete, removePriceProfile]);

	return (
		<PageContent title="Products">
			<div className="flex flex-1 flex-col gap-4 sm:gap-6 min-w-0">
				{/* Top Controls: Branch Selector & Add Product */}
				<div className="flex flex-wrap items-center justify-between gap-3">
					{/* Branch Selector */}
					<BranchSelector
						branches={branches}
						value={selectedBranchId}
						onValueChange={(val) => {
							if (!Array.isArray(val)) {
								setSelectedBranchId(val);
								resetPage();
							}
						}}
						placeholder="All branches"
						onViewAll={() => navigate({ to: "/settings/branches" })}
						onCreateNew={() => navigate({ to: "/settings/branches" })}
						className="w-48"
					/>

					<Can scope="products.create">
						<ActionHeaderButton
							label="Add Product"
							onClick={() => setCreateOpen(true)}
						/>
					</Can>
				</div>

				{/* Price Profile Cards */}
				<PriceProfileCards
					profiles={priceProfiles}
					productCounts={productCountsByProfile}
					productSummaries={profileProductSummaries}
					totalCount={allProducts.filter((p) => p.type !== "variant").length}
					noProfileCount={noProfileCount}
					selectedProfileId={selectedProfileId}
					onSelectProfile={handleProfileSelect}
					onAddProfile={() => setCreateProfileOpen(true)}
					onEditProfile={handleEditProfile}
					onDeleteProfile={handleDeleteProfile}
				/>

				{/* Search and Filters */}
				{!isLoading &&
					(allProducts.length > 0 ||
						searchQuery ||
						filterState.activeFilters.length > 0) && (
						<>
							<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
								<div className="flex-1 sm:max-w-sm">
									<SearchInput
										placeholder="Search products..."
										value={searchQuery}
										onChange={(e) => handleSearchChange(e.target.value)}
									/>
								</div>
								<div className="flex items-center gap-3">
									<Filters.Trigger
										filters={filterState.filters}
										activeFilters={filterState.activeFilters}
										onSelect={filterState.onSelect}
										onRemove={filterState.onRemove}
									/>
									<DisplaySelect
										viewMode={displaySettings.viewMode}
										onViewModeChange={(mode) => updateSetting("viewMode", mode)}
										selectedSort={displaySettings.sortKey}
										onSortChange={(sort) => updateSetting("sortKey", sort)}
										sortOptions={[
											{ value: "name", label: "Product Name" },
											{ value: "sku", label: "SKU" },
											{ value: "price", label: "Price" },
											{ value: "createdAt", label: "Date Created" },
										]}
										options={[
											{
												key: "showDeleted",
												label: "Show deleted",
												checked: displaySettings.showDeleted,
												icon: <Archive className="size-4" />,
											},
											{
												key: "showHeaders",
												label: "Show table headers",
												checked: displaySettings.showHeaders,
												icon: <LayoutPanelTop className="size-4" />,
											},
										]}
										onOptionsChange={(key, checked) => {
											if (key === "showDeleted")
												updateSetting("showDeleted", checked);
											if (key === "showHeaders")
												updateSetting("showHeaders", checked);
										}}
										properties={[
											{
												key: "category",
												label: "Category",
												visible: displaySettings.columnVisibility.category,
											},
											{
												key: "price",
												label: "Price",
												visible: displaySettings.columnVisibility.price,
											},
											{
												key: "status",
												label: "Status",
												visible: displaySettings.columnVisibility.status,
											},
											{
												key: "priceProfile",
												label: "Price Profile",
												visible: displaySettings.columnVisibility.priceProfile,
											},
											{
												key: "variants",
												label: "Variants",
												visible: displaySettings.columnVisibility.variants,
											},
										]}
										onPropertiesChange={updateColumnVisibility}
										onReset={handleReset}
										onSetDefault={handleSetDefault}
										showFooter={hasChanges}
									/>
									<Button
										variant="outline"
										onClick={() => setUploadModalOpen(true)}
										className="group flex items-center rounded-md border border-border bg-card px-5 py-3 transition-all hover:bg-muted/50 hover:border-border outline-none cursor-pointer select-none h-auto"
									>
										<FileUp
											size={18}
											strokeWidth={1.5}
											className="mr-3 text-neutral-800"
										/>
										<span className="text-sm font-medium text-neutral-900 leading-none">
											Upload
										</span>
									</Button>
								</div>
							</div>

							{/* Active Filter Pills */}
							<Filters.List
								filters={filterState.filterListItems}
								onRemove={filterState.onRemove}
								onClear={filterState.onClear}
							/>
						</>
					)}

				{/* Products Table or Empty State */}
				{!isLoading && filteredProducts.length === 0 ? (
					<div className="flex flex-1 flex-col justify-end pb-12">
						<EmptyStateWithSkeleton
							title="No products found"
							description="Add products to your catalog to start selling and managing inventory"
							actionLabel="Add Product"
							onAction={() => setCreateOpen(true)}
							icon={
								<HugeiconsIcon
									icon={Package01Icon}
									className="h-5 w-5 text-neutral-700"
									strokeWidth={2.2}
								/>
							}
						/>
					</div>
				) : (
					<ProductsTable
						data={paginatedProducts}
						categories={categories}
						priceProfiles={priceProfiles}
						branches={branches}
						loading={isLoading}
						pagination={pagination}
						onPaginationChange={onPaginationChange}
						rowCount={filteredProducts.length}
						onEdit={handleEdit}
						onRowClick={handleEdit}
						onDuplicate={handleDuplicate}
						onAddVariant={handleAddVariant}
						onDelete={handleDelete}
						onRestore={handleRestore}
						isRestoring={isRestoring}
						viewMode={displaySettings.viewMode}
						showHeaders={displaySettings.showHeaders}
						columnVisibility={displaySettings.columnVisibility}
					/>
				)}

				{/* Load More Button */}
				{productsQuery.status === "CanLoadMore" && (
					<div className="flex justify-center">
						<Button variant="outline" onClick={handleLoadMore}>
							Load More
						</Button>
					</div>
				)}
			</div>

			{/* Modals */}
			{uploadModalOpen && (
				<Suspense>
					<UploadProductsModal
						open={uploadModalOpen}
						onOpenChange={setUploadModalOpen}
						onDownloadTemplate={handleDownloadTemplate}
						onSave={async (_file, rows) => {
							if (!orgId) return;
							setIsUploadingProducts(true);
							let successCount = 0;
							let failCount = 0;
							try {
								for (const row of rows) {
									const name = String(row.Name ?? "").trim();
									const priceRaw = Number.parseFloat(String(row.Price ?? "0"));
									if (!name || Number.isNaN(priceRaw)) {
										failCount++;
										continue;
									}
									// Resolve category by name (case-insensitive)
									const categoryName = String(row.Category ?? "").trim();
									const matched = categories.find(
										(c) => c.name.toLowerCase() === categoryName.toLowerCase(),
									);
									// categoryId is required by the backend — skip rows with no match
									if (!matched) {
										failCount++;
										continue;
									}
									try {
										await createProduct({
											orgId,
											name,
											sku: String(row.Sku ?? "").trim() || undefined,
											description:
												String(row.Description ?? "").trim() || undefined,
											basePriceSatangs: toSatangs(priceRaw),
											categoryId: matched._id,
											isActive: true,
										});
										successCount++;
									} catch {
										failCount++;
									}
								}
								if (successCount > 0) {
									const plural = successCount === 1 ? "" : "s";
									const failNote =
										failCount > 0 ? ` (${failCount} failed)` : "";
									toast.success(
										<>
											{successCount} product{plural} uploaded successfully
											{failNote}
										</>,
									);
								} else {
									toast.error(
										<>
											No products were uploaded. Check your file and try again.
										</>,
									);
								}
								setUploadModalOpen(false);
							} finally {
								setIsUploadingProducts(false);
							}
						}}
						isSaving={isUploadingProducts}
					/>
				</Suspense>
			)}

			<CreateProductModal
				open={createOpen}
				onOpenChange={setCreateOpen}
				categories={categories}
				orgId={orgId}
				onSubmit={handleCreateProduct}
				isSubmitting={isCreating}
			/>

			<EditProductModal
				open={editOpen}
				onOpenChange={setEditOpen}
				product={selectedProduct}
				categories={categories}
				orgId={orgId}
				onSubmit={handleUpdateProduct}
				isSubmitting={isUpdating}
				initialImageUrl={
					selectedProduct?.imageUrls?.FULL ?? selectedProduct?.imageUrl
				}
			/>

			<CreateVariantModal
				key={selectedProduct?._id}
				open={variantOpen}
				onOpenChange={setVariantOpen}
				parentProduct={selectedProduct}
				onSubmit={handleCreateVariant}
				isSubmitting={isCreatingVariant}
			/>

			{/* Price Profile Modals */}
			<CreatePriceProfileModal
				open={createProfileOpen}
				onOpenChange={setCreateProfileOpen}
				branches={branches}
				onSubmit={handleCreatePriceProfile}
				isSubmitting={isCreatingProfile}
			/>

			<EditPriceProfileModal
				open={editProfileOpen}
				onOpenChange={setEditProfileOpen}
				profile={selectedProfile}
				branches={branches}
				onSubmit={handleUpdatePriceProfile}
				isSubmitting={isUpdatingProfile}
			/>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogMedia className="bg-red-50 text-red-600">
							<HugeiconsIcon icon={Delete02Icon} strokeWidth={2} />
						</AlertDialogMedia>
						<AlertDialogTitle>Delete product?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete{" "}
							<span className="font-medium text-neutral-900">
								"{productToDelete?.name}"?
							</span>{" "}
							This will remove the product from your active catalog and
							inventory. This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDelete}
							disabled={isDeleting}
							className="rounded-xl px-4"
						>
							{isDeleting ? "Deleting..." : "Delete Product"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Delete Price Profile Confirmation Dialog */}
			<AlertDialog
				open={profileDeleteDialogOpen}
				onOpenChange={(open) => {
					if (!open) setProfileToDelete(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogMedia className="bg-red-50 text-red-600">
							<HugeiconsIcon icon={Delete02Icon} strokeWidth={2} />
						</AlertDialogMedia>
						<AlertDialogTitle>Delete price profile?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete{" "}
							<span className="font-medium text-neutral-900">
								"{profileToDelete?.name}"?
							</span>{" "}
							Products currently using this profile must be reassigned first.
							This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeletingProfile}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDeleteProfile}
							disabled={isDeletingProfile}
							className="rounded-xl px-4"
						>
							{isDeletingProfile ? "Deleting..." : "Delete Profile"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContent>
	);
}
